/**
 * Shared API client for making authenticated HTTP requests with timeouts.
 *
 * All direct axios/fetch calls throughout the app should use this module
 * instead of calling axios or fetch directly. This ensures consistent
 * timeouts, auth headers, and error handling.
 */

import axios, { AxiosError, AxiosResponse } from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { Timeouts } from "@platform/constants/Common";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { StorageKeys } from "@platform/constants/Storage";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  statusCode?: number;
}

export class ApiRequestError extends Error {
  status: number;
  data?: ApiErrorResponse;

  constructor(message: string, status: number, data?: ApiErrorResponse) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.data = data;
  }
}

// ── Default config ─────────────────────────────────────────────────────────────

const DEFAULT_TIMEOUT = Timeouts.API_REQUEST; // 30s
const LONG_TIMEOUT = 60_000; // 60s for build/deploy operations

/**
 * Get the current auth token from OrgSessionManager (in-memory, never stale).
 * Falls back to localStorage if OrgSessionManager is not yet initialized.
 * Returns the Bearer token string or empty string.
 */
function getAuthHeader(): string {
  const token = orgSessionManager.getToken();
  return token ? `Bearer ${token}` : "";
}

// ── Shared axios instance ─────────────────────────────────────────────────────

const apiClient = axios.create({
  timeout: DEFAULT_TIMEOUT,
});

// Request interceptor: wait for any in-progress org switch before sending requests.
// This prevents stale step-1 tokens from leaking out during an org switch.
apiClient.interceptors.request.use(async (config) => {
  if (orgSessionManager.isSwitching()) {
    await orgSessionManager.waitForSwitch();
  }
  config.headers.set("Authorization", getAuthHeader());
  return config;
});

// Response interceptor for 401 handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth state and redirect to login
      localStorage.removeItem(StorageKeys.AUTH_TOKEN);
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ── Exported helpers ──────────────────────────────────────────────────────────

/**
 * Make an authenticated GET request with timeout.
 *
 * @param path - Full URL or path (if starts with /, prepended with apiBaseUrls.BACKEND)
 * @param options - Optional axios config overrides (e.g. custom timeout)
 */
export async function apiGet<T>(
  path: string,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.get<T>(url, {
      timeout,
      headers: {
        Authorization: getAuthHeader(),
        ...options?.headers,
      },
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated POST request with timeout.
 */
export async function apiPost<T, D = unknown>(
  path: string,
  data?: D,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.post<T>(url, data, {
      timeout,
      headers: {
        Authorization: getAuthHeader(),
        ...options?.headers,
      },
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated PATCH request with timeout.
 */
export async function apiPatch<T, D = unknown>(
  path: string,
  data?: D,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.patch<T>(url, data, {
      timeout,
      headers: {
        Authorization: getAuthHeader(),
        ...options?.headers,
      },
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated DELETE request with timeout.
 */
export async function apiDelete<T>(
  path: string,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  try {
    const response: AxiosResponse<T> = await apiClient.delete<T>(url, {
      timeout,
      headers: {
        Authorization: getAuthHeader(),
        ...options?.headers,
      },
    });
    return response.data;
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Make an authenticated fetch() request with AbortController timeout.
 * For use in components that prefer the fetch API over axios.
 */
export async function apiFetch<T>(
  path: string,
  options?: {
    method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
    body?: unknown;
    timeout?: number;
    headers?: Record<string, string>;
  },
): Promise<T> {
  const url = resolveUrl(path);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: options?.method ?? "GET",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
        ...options?.headers,
      },
      ...(options?.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiRequestError(
        body?.message ?? body?.error ?? `Request failed (${response.status})`,
        response.status,
        body,
      );
    }

    return (await response.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiRequestError(
        `Request timed out after ${timeout / 1000}s`,
        408,
      );
    }
    if (err instanceof ApiRequestError) throw err;
    throw normalizeError(err);
  } finally {
    clearTimeout(timeoutId);
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${apiBaseUrls.BACKEND}${path}`;
}

function normalizeError(err: unknown): ApiRequestError {
  if (err instanceof ApiRequestError) return err;

  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data as ApiErrorResponse | undefined;
    const message =
      data?.message ?? data?.error ?? err.message ?? "Request failed";
    return new ApiRequestError(message, status, data);
  }

  if (err instanceof Error) {
    if (err.name === "AbortError") {
      return new ApiRequestError("Request timed out", 408);
    }
    return new ApiRequestError(err.message, 0);
  }

  return new ApiRequestError("An unknown error occurred", 0);
}

// Export timeouts for use in components
export { DEFAULT_TIMEOUT, LONG_TIMEOUT };
